"use client";

import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";

/**
 * Real-time block status hook
 * Follows the same pattern as useRealtimeFollowStatus
 */

// Custom event for syncing block status across components
const BLOCK_STATUS_EVENT = "block-status-update";

// Event to notify follow components when a user is blocked/unblocked
export const USER_BLOCKED_EVENT = "user-blocked";
export const USER_UNBLOCKED_EVENT = "user-unblocked";

interface BlockData {
	id?: string;
	blocker_user_id: string;
	blocked_user_id: string;
	reason?: string | null;
	created_at?: string;
}

interface UseRealtimeBlockStatusOptions {
	currentUserId: string | null;
	targetUserId: string | null;
	enabled?: boolean;
}

/**
 * Real-time-first block status hook
 * Manages block status with real-time updates and optimistic UI
 */
export function useRealtimeBlockStatus({
	currentUserId,
	targetUserId,
	enabled = true,
}: UseRealtimeBlockStatusOptions) {
	// Local state is our single source of truth
	const [isBlocked, setIsBlocked] = useState(false);
	const [isBlockedBy, setIsBlockedBy] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const channelRef = useRef<any>(null);
	const instanceId = useRef(Math.random().toString(36).substring(7));
	const supabase = createClient();

	// Initial data fetch - happens ONCE
	useEffect(() => {
		if (!currentUserId || !targetUserId || !enabled) {
			setIsLoading(false);
			return;
		}

		async function fetchInitialData() {
			if (!currentUserId || !targetUserId) return;
			
			try {
				type BlockedUsersRow = Database["public"]["Tables"]["blocked_users"]["Row"];
				
				// Check if current user has blocked target
				const { data: blockData } = await supabase
					.from("blocked_users")
					.select("*")
					.eq("blocker_user_id", currentUserId)
					.eq("blocked_user_id", targetUserId)
					.maybeSingle() as { data: BlockedUsersRow | null; error: any };

				// Check if target has blocked current user
				const { data: reverseBlockData } = await supabase
					.from("blocked_users")
					.select("*")
					.eq("blocker_user_id", targetUserId)
					.eq("blocked_user_id", currentUserId)
					.maybeSingle() as { data: BlockedUsersRow | null; error: any };

				setIsBlocked(!!blockData);
				setIsBlockedBy(!!reverseBlockData);
			} catch (error) {
				// No block relationship exists
				setIsBlocked(false);
				setIsBlockedBy(false);
			} finally {
				setIsLoading(false);
			}
		}

		fetchInitialData();
	}, [currentUserId, targetUserId, enabled, supabase]);

	// Real-time subscription for block status changes
	useEffect(() => {
		if (!currentUserId || !targetUserId || !enabled) return;

		const channelName = `block-status:${currentUserId}:${targetUserId}:${instanceId.current}`;

		// Create a unique channel for this instance
		const channel = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "blocked_users",
					filter: `blocker_user_id=eq.${currentUserId}`,
				},
				(payload: RealtimePostgresChangesPayload<BlockData>) => {
					// Client-side filter for blocked_user_id (Supabase Realtime only supports 1 filter)
					const record = payload.eventType === "DELETE" ? payload.old : payload.new;
					if ((record as any)?.blocked_user_id !== targetUserId) {
						return;
					}

					// Real-time update for blocks by current user
					if (payload.eventType === "DELETE") {
						setIsBlocked(false);
					} else if (
						payload.eventType === "INSERT" ||
						payload.eventType === "UPDATE"
					) {
						setIsBlocked(true);
					}
				},
			)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "blocked_users",
					filter: `blocker_user_id=eq.${targetUserId}`,
				},
				(payload: RealtimePostgresChangesPayload<BlockData>) => {
					// Client-side filter for blocked_user_id (Supabase Realtime only supports 1 filter)
					const record = payload.eventType === "DELETE" ? payload.old : payload.new;
					if ((record as any)?.blocked_user_id !== currentUserId) {
						return;
					}

					// Real-time update for blocks by target user
					if (payload.eventType === "DELETE") {
						setIsBlockedBy(false);
					} else if (
						payload.eventType === "INSERT" ||
						payload.eventType === "UPDATE"
					) {
						setIsBlockedBy(true);
					}
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully - errors are silently handled
				if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
					// Will retry on reconnect
				}
			});

		channelRef.current = channel;

		return () => {
			// Clean up this instance's channel
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [currentUserId, targetUserId, enabled, supabase]);

	// Listen for optimistic updates from other component instances
	useEffect(() => {
		if (!currentUserId || !targetUserId) return;

		const handleOptimisticUpdate = (event: CustomEvent) => {
			const { userId, targetId, blocked } = event.detail;
			// Check if this event is for our block relationship
			if (userId === currentUserId && targetId === targetUserId) {
				setIsBlocked(blocked);
			}
		};

		// Listen for custom events from other component instances
		window.addEventListener(
			BLOCK_STATUS_EVENT as any,
			handleOptimisticUpdate as any,
		);

		return () => {
			window.removeEventListener(
				BLOCK_STATUS_EVENT as any,
				handleOptimisticUpdate as any,
			);
		};
	}, [currentUserId, targetUserId]);

	// Function to set optimistic status and broadcast to other instances
	const setOptimisticStatus = useCallback(
		(blocked: boolean) => {
			// Update local state immediately
			setIsBlocked(blocked);

			// Broadcast to other component instances
			const blockEvent = new CustomEvent(BLOCK_STATUS_EVENT, {
				detail: {
					userId: currentUserId,
					targetId: targetUserId,
					blocked,
				},
			});
			window.dispatchEvent(blockEvent);

			// Also broadcast to follow components when blocking/unblocking
			const eventType = blocked ? USER_BLOCKED_EVENT : USER_UNBLOCKED_EVENT;
			const userEvent = new CustomEvent(eventType, {
				detail: {
					blockerUserId: currentUserId,
					targetUserId: targetUserId,
				},
			});
			window.dispatchEvent(userEvent);
		},
		[currentUserId, targetUserId],
	);

	return {
		isBlocked,
		isBlockedBy,
		isLoading,
		setOptimisticStatus,
	};
}
